/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */

/*
 * NotifySetting.jsx — ตั้งค่าแจ้งเตือน Telegram   (งานข้อ 2 · Step 3.5)
 *
 * เมนูใหม่ในกลุ่มตั้งค่าระบบ (menus.code = notify_setting — migration 0003)
 *
 * หน้านี้ทำ 2 อย่าง
 *   1) จัดการ "ปลายทาง" — กลุ่มไหน เลขห้องแชทอะไร ทดสอบส่งได้
 *   2) ตารางผูก "เหตุการณ์ × ปลายทาง" — ติ๊กช่องเดียวเปิด/ปิดได้ทันที
 *      เหตุการณ์เดียวส่งได้หลายกลุ่ม กลุ่มเดียวรับได้หลายเหตุการณ์
 *
 * ทำไมออกแบบเป็นตารางติ๊ก ไม่ใช่ dropdown
 *   ความสัมพันธ์เป็นแบบหลาย-ต่อ-หลาย ตารางติ๊กเห็นภาพรวมทั้งหมดในหน้าจอเดียว
 *   ว่าเหตุการณ์ไหนจะเข้ากลุ่มไหนบ้าง — สำคัญเพราะส่งผิดกลุ่ม = ข้อมูลภายในหลุด
 *
 * แก้ค่าต้องมีสิทธิ์ notify_setting (backend เช็คซ้ำเสมอ)
 */

import React, { useEffect, useRef, useState } from 'react';

import Grid from '@mui/material/Grid';
import Icon from '@mui/material/Icon';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDSnackbar from 'components/MDSnackbar';
import NotifyTemplateDialog from 'layouts/_entrio/NotifyTemplateDialog';
import NotifySendDialog from 'layouts/_entrio/NotifySendDialog';
import NotifyEventDialog from 'layouts/_entrio/NotifyEventDialog';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initChannel = { id: '', code: '', name: '', channel: 'TELEGRAM', chat_id: '', note: '' };

const NO_PERMISSION = 'บัญชีนี้ไม่มีสิทธิ์แก้การตั้งค่า';
const NO_BOT = 'ยังไม่ได้ตั้งค่าบอต (แจ้งไอที)';
const NET_ERROR = 'เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ลองใหม่อีกครั้ง';

/* ข้อความ error จาก Telegram (อังกฤษ) และหมายเหตุเทคนิคในประวัติ → ไทย (18 ต.ค. 2569)
   แทนเฉพาะส่วนที่รู้จัก ข้อความไทยรอบ ๆ (ส่งซ้ำสำเร็จ … · เดิม: …) คงไว้ · ข้อความดิบโชว์ใน title */
const TELEGRAM_ERRORS = [
  [/ไม่มี TELEGRAM_BOT_TOKEN ใน \.env/g, NO_BOT],
  [/Bad Request: chat not found/gi, 'เลขห้องผิดหรือบอตไม่อยู่ในกลุ่ม'],
  [/Forbidden: bot was kicked from the (?:super)?group chat/gi, 'บอตถูกนำออกจากกลุ่ม'],
  [/Forbidden: bot is not a member of the (?:super)?group chat/gi, 'บอตไม่อยู่ในกลุ่ม'],
  [/Forbidden: bot was blocked by the user/gi, 'บอตถูกบล็อก'],
  [/Forbidden: the group chat was deleted/gi, 'กลุ่มถูกลบแล้ว'],
  [
    /Bad Request: not enough rights to send text messages to the chat/gi,
    'บอตไม่มีสิทธิ์ส่งข้อความในกลุ่ม',
  ],
  [
    /Bad Request: group chat was upgraded to a supergroup chat/gi,
    'กลุ่มถูกอัปเกรดเป็นซูเปอร์กรุ๊ป',
  ],
  [/Bad Request: can't parse entities[^)·]*/gi, 'รูปแบบข้อความผิด'],
  [/Too Many Requests(?:: retry after \d+)?/gi, 'ส่งถี่เกิน รอสักครู่'],
  [/Unauthorized/g, 'โทเคนบอตไม่ถูกต้อง'],
  [/\bNot Found\b/g, 'โทเคนบอตไม่ถูกต้อง'],
];
const telegramText = (raw) =>
  TELEGRAM_ERRORS.reduce((s, [re, th]) => s.replace(re, th), String(raw || ''));

/* รหัสจาก backend ตอนทดสอบส่ง/ส่งซ้ำ → ไทย (ไม่โชว์รหัสดิบ) */
const SEND_ERROR_TEXT = {
  permission_denied: NO_PERMISSION,
  telegram_token_missing: NO_BOT,
  channel_not_found: 'ไม่พบกลุ่มนี้ หรือกลุ่มถูกปิดอยู่',
  channel_disabled: 'กลุ่มนี้ถูกปิดใช้งาน เปิดกลุ่มก่อนแล้วค่อยส่งซ้ำ',
  log_not_found: 'ไม่พบรายการนี้แล้ว กดรีเฟรชประวัติ',
};
const sendErrorText = (r) =>
  SEND_ERROR_TEXT[r.msg] || `ส่งไม่สำเร็จ: ${telegramText(r.detail) || 'ไม่ทราบสาเหตุ'}`;

/* created_at จากฐานเป็น "YYYY-MM-DD HH:mm:ss" — ใส่ T ให้ทุกเบราว์เซอร์อ่านเป็นเวลาท้องถิ่น */
const whenText = (v) => (v ? Func.dateTimeDisplay(String(v).replace(' ', 'T')) : '-');

const sectionSx = {
  border: '1px solid',
  borderColor: 'grey.300',
  borderRadius: 2,
  p: 3,
  bgcolor: '#ffffff',
};

const eyebrow = (text) => (
  <MDTypography
    variant='caption'
    fontWeight='bold'
    color='text'
    sx={{ letterSpacing: 1.5, textTransform: 'uppercase', opacity: 0.7 }}
  >
    {text}
  </MDTypography>
);

function NotifySetting() {
  const mod = 'notify_setting';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();

  const [channels, setChannels] = useState([]);
  const [events, setEvents] = useState([]);
  const [routing, setRouting] = useState({});
  const [canEdit, setCanEdit] = useState(false);
  /* ข้อความแจ้งเตือนแก้เองได้ (A5 · 25 ต.ค. 2569) — รายการแม่แบบจาก OPTIONS /templates · tplEvent = เหตุการณ์ที่เปิดหน้าต่างแก้อยู่ */
  const [templates, setTemplates] = useState([]);
  const [tplReady, setTplReady] = useState(false);
  const [tplEvent, setTplEvent] = useState(null);
  /* ส่งข้อความเองเข้ากลุ่ม (A6 · 25 ต.ค. 2569) — manualReady = รัน 0030 แล้ว */
  const [sendOpen, setSendOpen] = useState(false);
  const [manualReady, setManualReady] = useState(false);
  /* เหตุการณ์ที่ร้านเพิ่มเอง (25 ต.ค. 2569) — customReady = รัน 0031 แล้ว */
  const [customReady, setCustomReady] = useState(false);
  const [evDialog, setEvDialog] = useState(null); // { event: null|ev, message } = เปิดกล่องเพิ่ม/แก้
  const [evConfirm, setEvConfirm] = useState(null); // { mode: 'send'|'delete', ev } รอยืนยัน
  const [evBusy, setEvBusy] = useState(false);
  const [hasToken, setHasToken] = useState(''); // '' = ยังโหลดไม่เสร็จ ไม่โชว์ป้ายแดง (รีเช็ค 27 ต.ค. 2569)
  const [last7, setLast7] = useState(null);
  const [logs, setLogs] = useState([]);

  const [form, setForm] = useState(null); // null = ปิด, object = เปิดกล่องเพิ่ม/แก้
  const [notify, setNotify] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testRow, setTestRow] = useState(null);
  const [sending, setSending] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [resendingId, setResendingId] = useState(0); // แถวประวัติที่กำลังส่งซ้ำ (0 = ไม่มี)
  const [confirmRow, setConfirmRow] = useState(null); // กลุ่มที่รอยืนยันปิด/เปิด { ...channel, on }
  const sendingRef = useRef(false);
  const notifyTimer = useRef(null);

  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const showNotify = (msg, color) => {
    setNotify(
      <MDSnackbar
        color={color}
        icon='notifications'
        title={t(`app.name`)}
        content={msg}
        dateTime={Func.dateTimeDisplay()}
        open={Boolean(true)}
        close={() => setNotify(null)}
      />
    );
    /* ตัวจับเวลาเดียว — ไม่ให้ตัวเก่าของแถบสำเร็จปิดแถบ error ที่ขึ้นตามมาก่อนเวลา */
    clearTimeout(notifyTimer.current);
    notifyTimer.current = setTimeout(
      function () {
        if (alive.current) setNotify(null);
      },
      color === 'error' ? 8000 : 3000
    );
  };

  const load = () => {
    setLoading(true);
    const fail = () => ({ status: 'fail', msg: 'network_error' });
    Promise.all([
      FetchData('GET', `/${mod}`, app.token, {}).catch(fail),
      FetchData('POST', `/${mod}`, app.token, { limit: 20, _t: Date.now() }).catch(fail),
      FetchData('OPTIONS', `/${mod}/templates`, app.token, { _t: Date.now() }).catch(fail),
      FetchData('OPTIONS', `/${mod}/messages`, app.token, { _t: Date.now() }).catch(fail),
    ]).then(([init, view, tpl, man]) => {
      if (!alive.current) return;
      setLoading(false);

      if (init.status === 'ok') {
        setChannels(init.channels || []);
        setEvents(init.events || []);
        setRouting(init.routing || {});
        setCanEdit(init.can_edit === 'Y');
        setHasToken(init.has_token);
        setLast7(init.last7days);
        setCustomReady(init.custom_ready === 'Y');
      }
      if (view.status === 'ok') setLogs(view.data || []);
      if (tpl.status === 'ok') {
        setTemplates(tpl.data || []);
        setTplReady(tpl.ready === 'Y');
      }
      if (man.status === 'ok') setManualReady(man.ready === 'Y');

      if (init.status !== 'ok' || view.status !== 'ok') {
        const net = init.msg === 'network_error' || view.msg === 'network_error';
        showNotify(net ? NET_ERROR : t(`alert.loaderror`), 'error');
      }
    });
  };

  /* โหลดเฉพาะรายการแม่แบบ หลังบันทึก (A5) */
  const reloadTemplates = () => {
    FetchData('OPTIONS', `/${mod}/templates`, app.token, { _t: Date.now() })
      .then((tpl) => {
        if (alive.current && tpl.status === 'ok') setTemplates(tpl.data || []);
      })
      .catch(() => {});
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    load();
  }, []);

  const toggleRoute = (eventCode, channelId, on) => {
    /* อัปเดตหน้าจอทันทีให้กดแล้วรู้สึกตอบสนอง แล้วค่อยยืนยันกับ backend */
    const key = `${eventCode}|${channelId}`;
    setRouting((cur) => ({ ...cur, [key]: on ? 'Y' : 'N' }));
    const revert = () => setRouting((cur) => ({ ...cur, [key]: on ? 'N' : 'Y' })); // ย้อนกลับถ้าไม่สำเร็จ

    FetchData('OPTIONS', `/${mod}/route`, app.token, {
      event_code: eventCode,
      channel_id: channelId,
      enable: on ? 'Y' : 'N',
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current) return;
        if (r.status !== 'ok') {
          revert();
          showNotify(r.msg === 'permission_denied' ? NO_PERMISSION : t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        if (!alive.current) return;
        revert();
        showNotify(NET_ERROR, 'error');
      });
  };

  const saveChannel = () => {
    if (!form) return;
    if (!form.name.trim() || !form.chat_id.trim()) {
      showNotify('ใส่ชื่อกลุ่มและเลขห้องแชทก่อน', 'warning');
      return;
    }

    setLoading(true);
    const isEdit = Boolean(form.id);
    const payload = {
      name: form.name,
      channel: form.channel,
      chat_id: form.chat_id,
      note: form.note,
    };
    if (!isEdit) payload.code = form.code;

    FetchData(
      isEdit ? 'PATCH' : 'PUT',
      isEdit ? `/${mod}/${form.id}` : `/${mod}`,
      app.token,
      payload
    )
      .then((r) => {
        if (!alive.current) return;
        setLoading(false);
        if (r.status === 'ok') {
          const channelId = isEdit ? Number(form.id) : Number(r.id || 0);
          const codes = form.events || [];
          const before = form.events0 || [];
          /* ส่งเฉพาะที่ติ๊ก/เอาออกในหน้าต่างนี้ (รีเช็ค 26 ต.ค.: เดิมส่งทั้งชุดจากหน้าที่โหลดไว้
             ถ้าผู้ดูแลอีกคนเพิ่งเปลี่ยนเหตุการณ์ของกลุ่มนี้ การบันทึกจากหน้าเก่าจะย้อนของเขาเงียบ ๆ) */
          const on = codes.filter((c) => !before.includes(c));
          const off = before.filter((c) => !codes.includes(c));
          setForm(null);
          const routes =
            channelId && (on.length || off.length)
              ? FetchData('OPTIONS', `/${mod}/set_routes`, app.token, {
                  channel_id: channelId,
                  on,
                  off,
                  _t: Date.now(),
                }).catch(() => ({ status: 'fail' }))
              : Promise.resolve({ status: 'ok', event_codes: null });
          routes.then((rr) => {
            if (!alive.current) return;
            const count = Array.isArray(rr.event_codes) ? rr.event_codes.length : codes.length;
            showNotify(
              rr.status === 'ok'
                ? `บันทึกกลุ่มแล้ว · รับแจ้ง ${count} เรื่อง`
                : 'บันทึกกลุ่มแล้ว แต่ตั้งเหตุการณ์ไม่สำเร็จ — ติ๊กในตารางแทน',
              rr.status === 'ok' ? 'success' : 'warning'
            );
            load();
          });
        } else if (r.msg === 'code_duplicate') {
          showNotify('รหัสนี้มีอยู่แล้ว', 'error');
        } else if (r.msg === 'permission_denied') {
          showNotify(NO_PERMISSION, 'error');
        } else {
          showNotify(t(`alert.saveerror`), 'error');
        }
      })
      .catch(() => {
        if (!alive.current) return;
        setLoading(false); // กล่องฟอร์มยังเปิดอยู่ กดบันทึกซ้ำได้
        showNotify(NET_ERROR, 'error');
      });
  };

  const canTest = (row) =>
    canEdit &&
    hasToken === 'Y' &&
    row &&
    row.enable === 'Y' &&
    row.channel === 'TELEGRAM' &&
    Boolean(row.chat_id);

  /* เหตุผลที่ปุ่มทดสอบส่งกดไม่ได้ — เป็นข้อความใต้ปุ่ม (title บนปุ่มที่ปิดอยู่ชี้แล้วไม่ขึ้น) */
  const testBlockReason = (row) => {
    if (row.enable !== 'Y') return '';
    if (hasToken !== 'Y') return NO_BOT;
    if (row.channel !== 'TELEGRAM') return 'LINE ยังส่งไม่ได้';
    if (!row.chat_id) return 'ยังไม่มีเลขห้อง';
    return '';
  };

  /* ปิดกล่องยืนยันแบบคงข้อมูลไว้จนจบแอนิเมชัน — ไม่ให้หัวกล่องสลับเป็นอีกคำระหว่างจางหาย */
  const closeConfirm = () => setConfirmRow((cur) => (cur ? { ...cur, closing: true } : cur));

  const routedCount = (row) =>
    events.filter((ev) => routing[`${ev.code}|${row.id}`] === 'Y').length;

  /* เหตุการณ์ที่ร้านเพิ่มเอง: เปิดกล่องแก้พร้อมข้อความปัจจุบัน · ส่งตอนนี้ · ลบ */
  const DAY_SHORT = ['', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส', 'อา'];
  const scheduleText = (ev) => {
    if (!ev.schedule_time) return '';
    const days = String(ev.schedule_days || '')
      .split(',')
      .filter(Boolean)
      .map((d) => DAY_SHORT[Number(d)] || '')
      .join(' ');
    return `ส่งอัตโนมัติ ${String(ev.schedule_time).slice(0, 5)} · ${
      !days || days.split(' ').length === 7 ? 'ทุกวัน' : days
    }`;
  };
  const routedGroups = (ev) =>
    channels.filter((c) => c.enable === 'Y' && routing[`${ev.code}|${c.id}`] === 'Y').length;
  const openEvEdit = (ev) => {
    const tp = templates.find((x) => x.code === ev.code);
    setEvDialog({ event: ev, message: tp ? tp.template : '' });
  };
  const closeEvConfirm = () => setEvConfirm((cur) => (cur ? { ...cur, closing: true } : cur));
  const runEvConfirm = () => {
    if (!evConfirm || evBusy) return;
    const { mode, ev } = evConfirm;
    setEvBusy(true);
    FetchData('OPTIONS', `/${mod}/${mode === 'send' ? 'send_event' : 'delete_event'}`, app.token, {
      code: ev.code,
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current) return;
        setEvBusy(false);
        closeEvConfirm();
        if (r.status !== 'ok') {
          showNotify(
            r.msg === 'telegram_token_missing'
              ? NO_BOT
              : r.msg === 'message_required'
              ? 'เหตุการณ์นี้ยังไม่มีข้อความ กด "แก้" แล้วพิมพ์ข้อความก่อน'
              : r.msg === 'event_not_found'
              ? 'ไม่พบเหตุการณ์นี้แล้ว โหลดหน้าใหม่'
              : mode === 'send'
              ? sendErrorText(r)
              : t(`alert.deleteerror`),
            'error'
          );
          return;
        }
        if (mode === 'delete') {
          showNotify(t(`alert.deletecomplete`), 'success');
        } else if (r.skipped === 'Y') {
          showNotify('ยังไม่ได้ติ๊กกลุ่มที่จะรับ — ติ๊กในตารางก่อนแล้วกดส่งอีกครั้ง', 'warning');
        } else if (Number(r.failed) > 0) {
          showNotify(
            `ส่งเข้า ${r.sent} กลุ่ม · ไม่สำเร็จ ${r.failed} กลุ่ม — ดูสาเหตุในประวัติการส่ง`,
            'error'
          );
        } else {
          showNotify(`ส่งแล้ว ${r.sent} กลุ่ม`, 'success');
        }
        load();
      })
      .catch(() => {
        if (alive.current) {
          setEvBusy(false);
          closeEvConfirm();
          /* ส่งตอนนี้: เซิร์ฟเวอร์อาจส่งไปบางกลุ่มแล้ว — ไม่ชวนกดซ้ำ (กันข้อความซ้ำในกลุ่ม) */
          showNotify(
            mode === 'send'
              ? 'ไม่ทราบผลการส่ง — ดูในกลุ่มหรือประวัติการส่งก่อนกดอีกครั้ง'
              : NET_ERROR,
            mode === 'send' ? 'warning' : 'error'
          );
          if (mode === 'send') load();
        }
      });
  };

  /* ส่งซ้ำข้อความที่ไม่สำเร็จ (Entrio 16 ต.ค. 2569) — ทีละแถว · สำเร็จแล้วแถวเป็น "ส่งแล้ว" ปุ่มหาย */
  const canResend = (l) =>
    canEdit &&
    hasToken === 'Y' &&
    l.status === 'FAILED' &&
    Number(l.channel_id) > 0 &&
    l.channel_enable === 'Y';

  const resendLog = (l) => {
    if (!canResend(l) || resendingId) return;
    setResendingId(l.id);
    FetchData('OPTIONS', `/${mod}/resend`, app.token, { id: l.id, _t: Date.now() })
      .then((r) => {
        if (!alive.current) return;
        if (r.status === 'ok') {
          showNotify(`ส่งซ้ำเข้ากลุ่ม "${r.channel || l.channel_name}" แล้ว`, 'success');
        } else if (r.msg === 'not_failed') {
          showNotify('รายการนี้ส่งสำเร็จแล้ว', 'info');
        } else {
          showNotify(sendErrorText(r), 'error');
        }
        load();
      })
      .catch(() => {
        if (alive.current)
          showNotify('ไม่ทราบผลการส่ง ตรวจในกลุ่มแล้วกดรีเฟรชประวัติก่อนลองใหม่', 'error');
      })
      .finally(() => {
        if (alive.current) setResendingId(0);
      });
  };

  const testSend = () => {
    const row = testRow;
    if (!canTest(row) || sendingRef.current) return;
    sendingRef.current = true;
    setSending(true);
    FetchData('OPTIONS', `/${mod}/test`, app.token, { id: row.id, _t: Date.now() })
      .then((r) => {
        if (!alive.current) return;
        setTestRow(null);
        if (r.status === 'ok') {
          showNotify(`ส่งข้อความทดสอบเข้ากลุ่ม "${row.name}" แล้ว`, 'success');
        } else {
          showNotify(sendErrorText(r), 'error');
        }
        load();
      })
      .catch(() => {
        if (alive.current)
          showNotify('ไม่ทราบผลการส่ง ตรวจในกลุ่มแล้วกดรีเฟรชประวัติก่อนลองใหม่', 'error');
      })
      .finally(() => {
        sendingRef.current = false;
        if (alive.current) setSending(false);
      });
  };

  /* ปิด = DELETE (backend เปลี่ยน enable เป็น N ไม่ลบแถว) · เปิดกลับ = PATCH enable Y
     ทั้งสองทางผ่านกล่องยืนยันก่อนเสมอ — เปิดกลับแล้วเรื่องที่ผูกไว้ส่งเข้ากลุ่มจริงทันที (18 ต.ค. 2569) */
  const setChannelEnable = (row) => {
    if (!row || row.closing) return; // กดซ้ำระหว่างกล่องกำลังปิด
    const { on } = row;
    const action = on ? 'เปิดใช้งาน' : 'ปิดใช้งาน';
    closeConfirm();
    setLoading(true);
    const req = on
      ? FetchData('PATCH', `/${mod}/${row.id}`, app.token, { enable: 'Y', _t: Date.now() })
      : FetchData('DELETE', `/${mod}/${row.id}`, app.token, { _t: Date.now() });
    req
      .then((r) => {
        if (!alive.current) return;
        setLoading(false);
        if (r.status === 'ok') {
          showNotify(`${action}กลุ่ม "${row.name}" แล้ว`, 'success');
          load();
        } else {
          showNotify(r.msg === 'permission_denied' ? NO_PERMISSION : `${action}ไม่สำเร็จ`, 'error');
        }
      })
      .catch(() => {
        if (!alive.current) return;
        setLoading(false);
        showNotify(`${action}ไม่สำเร็จ · ${NET_ERROR}`, 'error');
      });
  };

  const activeChannels = channels.filter((c) => c.enable === 'Y');

  return (
    <DashboardLayout>
      <Navbar route={[t(`menu.setting`), 'ตั้งค่าแจ้งเตือน Telegram']} />

      <MDBox my={2}>
        {/* ── หัวหน้า ────────────────────────────────────────────────── */}
        <MDBox
          display='flex'
          alignItems='flex-start'
          justifyContent='space-between'
          flexWrap='wrap'
          gap={1}
          mb={2}
        >
          <MDBox>
            <MDTypography variant='h5' fontWeight='medium'>
              ตั้งค่าแจ้งเตือน Telegram
            </MDTypography>
            <MDTypography variant='body2' color='text' sx={{ opacity: 0.8 }}>
              เลือกว่าเรื่องไหนแจ้งเข้ากลุ่มไหน
            </MDTypography>
          </MDBox>
          <MDBox display='flex' gap={1} alignItems='center' flexWrap='wrap'>
            {last7 && (
              <Chip
                size='small'
                variant='outlined'
                label={`7 วัน: ส่งแล้ว ${last7.sent} · ไม่สำเร็จ ${last7.failed} · ไม่มีปลายทาง ${last7.skipped}`}
              />
            )}
            {!canEdit && <Chip size='small' variant='outlined' label='บัญชีนี้ดูได้อย่างเดียว' />}
          </MDBox>
        </MDBox>

        {hasToken === 'N' && (
          <MDBox
            mb={2}
            p={2}
            sx={{
              border: '1px solid',
              borderColor: 'error.main',
              borderRadius: 2,
              bgcolor: '#FDECEA',
            }}
          >
            <MDTypography variant='button' fontWeight='bold' color='error'>
              ระบบยังส่งข้อความไม่ได้
            </MDTypography>
            <MDTypography variant='caption' color='text' display='block' sx={{ opacity: 0.85 }}>
              {NO_BOT} · การตั้งค่าในหน้านี้บันทึกได้ตามปกติ
            </MDTypography>
            <MDButton variant='text' color='info' size='small' onClick={() => setHelpOpen(true)}>
              เปิดคู่มือสำหรับไอที
            </MDButton>
          </MDBox>
        )}

        <Grid container spacing={3}>
          {/* ── ปลายทาง ────────────────────────────────────────────── */}
          <Grid item xs={12} md={5}>
            <MDBox sx={sectionSx}>
              <MDBox display='flex' alignItems='center' justifyContent='space-between'>
                {eyebrow('ปลายทาง')}
                {canEdit && (
                  <MDBox display='flex' gap={1}>
                    {manualReady && hasToken === 'Y' && (
                      <MDButton
                        size='small'
                        variant='gradient'
                        color='info'
                        onClick={() => setSendOpen(true)}
                        aria-label='ส่งข้อความเองเข้ากลุ่ม'
                      >
                        <Icon>send</Icon>&nbsp;ส่งข้อความ
                      </MDButton>
                    )}
                    <MDButton
                      size='small'
                      variant='outlined'
                      color='dark'
                      onClick={() =>
                        setForm({
                          ...initChannel,
                          code: `ch_${Date.now()}`,
                          events: [],
                          events0: [],
                        })
                      }
                    >
                      <Icon>add</Icon>&nbsp;เพิ่มกลุ่ม
                    </MDButton>
                  </MDBox>
                )}
              </MDBox>

              <MDBox mt={2} display='flex' flexDirection='column' gap={1.5}>
                {channels.map((c) => (
                  <MDBox
                    key={c.id}
                    p={1.5}
                    sx={{
                      border: '1px solid',
                      borderColor: 'grey.300',
                      borderRadius: 2,
                    }}
                  >
                    {/* จางเฉพาะข้อมูลกลุ่มที่ปิด — ปุ่มเปิดใช้งานต้องชัด */}
                    <MDBox sx={{ opacity: c.enable === 'Y' ? 1 : 0.5 }}>
                      <MDBox display='flex' alignItems='center' gap={1} flexWrap='wrap'>
                        <MDTypography variant='button' fontWeight='bold'>
                          {c.name}
                        </MDTypography>
                        <Chip size='small' variant='outlined' label={c.channel} />
                        {c.enable === 'N' && (
                          <Chip size='small' color='secondary' label='ปิดใช้งาน' />
                        )}
                      </MDBox>
                      <MDTypography
                        variant='caption'
                        color='text'
                        display='block'
                        sx={{ opacity: 0.8 }}
                      >
                        เลขห้อง {c.chat_id}
                      </MDTypography>
                      {c.note && (
                        <MDTypography
                          variant='caption'
                          color='text'
                          display='block'
                          sx={{ opacity: 0.6 }}
                        >
                          {c.note}
                        </MDTypography>
                      )}
                    </MDBox>
                    {canEdit && (
                      <MDBox
                        mt={1}
                        display='flex'
                        gap={0.5}
                        justifyContent='flex-end'
                        flexWrap='wrap'
                      >
                        {c.enable === 'Y' ? (
                          <MDButton
                            size='small'
                            variant='text'
                            color='error'
                            onClick={() => setConfirmRow({ ...c, on: false })}
                          >
                            ปิดใช้งาน
                          </MDButton>
                        ) : (
                          <MDButton
                            size='small'
                            variant='outlined'
                            color='info'
                            onClick={() => setConfirmRow({ ...c, on: true })}
                          >
                            <Icon>power_settings_new</Icon>&nbsp;เปิดใช้งาน
                          </MDButton>
                        )}
                        <MDButton
                          size='small'
                          variant='text'
                          color='dark'
                          onClick={() =>
                            setForm(() => {
                              const on = events
                                .filter((ev) => routing[`${ev.code}|${c.id}`] === 'Y')
                                .map((ev) => ev.code);
                              return { ...initChannel, ...c, events: on, events0: on };
                            })
                          }
                        >
                          <Icon>edit</Icon>&nbsp;แก้ไข
                        </MDButton>
                        {c.enable === 'Y' && (
                          <MDButton
                            size='small'
                            variant='outlined'
                            color='dark'
                            disabled={!canTest(c) || sending}
                            onClick={() => setTestRow(c)}
                          >
                            <Icon>send</Icon>&nbsp;ทดสอบส่ง
                          </MDButton>
                        )}
                      </MDBox>
                    )}
                    {canEdit && testBlockReason(c) && (
                      <MDTypography
                        variant='caption'
                        color='text'
                        display='block'
                        textAlign='right'
                        mt={0.5}
                        sx={{ opacity: 0.8 }}
                      >
                        ทดสอบส่งไม่ได้: {testBlockReason(c)}
                      </MDTypography>
                    )}
                  </MDBox>
                ))}
                {channels.length === 0 && (
                  <MDTypography variant='body2' color='text' sx={{ opacity: 0.7 }}>
                    ยังไม่มีปลายทาง — กด &quot;เพิ่มกลุ่ม&quot; แล้วใส่เลขห้องแชท
                  </MDTypography>
                )}
              </MDBox>
            </MDBox>
          </Grid>

          {/* ── ตารางผูกเหตุการณ์ × ปลายทาง ────────────────────────── */}
          <Grid item xs={12} md={7}>
            <MDBox sx={sectionSx}>
              <MDBox display='flex' alignItems='center' justifyContent='space-between'>
                {eyebrow('เหตุการณ์ไหนส่งเข้ากลุ่มไหน')}
                {canEdit && customReady && tplReady && (
                  <MDButton
                    size='small'
                    variant='outlined'
                    color='dark'
                    onClick={() => setEvDialog({ event: null, message: '' })}
                    aria-label='เพิ่มเหตุการณ์ของร้าน'
                  >
                    <Icon>add</Icon>&nbsp;เพิ่มเหตุการณ์
                  </MDButton>
                )}
              </MDBox>
              <MDTypography
                variant='caption'
                color='text'
                display='block'
                mt={0.5}
                sx={{ opacity: 0.75 }}
              >
                ติ๊กเพื่อเปิดการส่ง · เรื่องเดียวส่งได้หลายกลุ่ม
                {canEdit && customReady && tplReady && ' · เพิ่มเหตุการณ์ของร้านเองได้'}
              </MDTypography>

              <MDBox mt={2} sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <TableHead sx={{ display: 'table-header-group' }}>
                    <TableRow>
                      <TableCell>เหตุการณ์</TableCell>
                      {activeChannels.map((c) => (
                        <TableCell key={c.id} align='center'>
                          {c.name}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {events.map((ev) => (
                      <TableRow key={ev.code}>
                        <TableCell>
                          <MDTypography variant='button' fontWeight='medium'>
                            {ev.name}
                          </MDTypography>
                          {ev.custom === 'Y' && (
                            <Chip
                              size='small'
                              variant='outlined'
                              color='info'
                              label='ของร้าน'
                              sx={{ ml: 1, height: 20, fontSize: 11 }}
                            />
                          )}
                          <MDTypography
                            variant='caption'
                            color='text'
                            display='block'
                            sx={{ opacity: 0.7 }}
                          >
                            {ev.description}
                          </MDTypography>
                          {ev.custom === 'Y' && scheduleText(ev) && (
                            <MDTypography
                              variant='caption'
                              display='block'
                              sx={{ opacity: 0.85 }}
                              title={
                                ev.last_sent_at
                                  ? `ส่งล่าสุด ${whenText(ev.last_sent_at)}`
                                  : 'ยังไม่เคยส่ง'
                              }
                            >
                              <Icon sx={{ fontSize: '14px !important', verticalAlign: '-2px' }}>
                                schedule
                              </Icon>{' '}
                              {scheduleText(ev)}
                            </MDTypography>
                          )}
                          {ev.custom === 'Y' && canEdit && (
                            <MDBox display='flex' gap={0.5} mt={0.25} flexWrap='wrap'>
                              <MDButton
                                size='small'
                                variant='text'
                                color='info'
                                disabled={hasToken !== 'Y'}
                                title={hasToken !== 'Y' ? NO_BOT : undefined}
                                aria-label={`ส่งตอนนี้ ${ev.name}`}
                                onClick={() => setEvConfirm({ mode: 'send', ev })}
                                sx={{ px: 0.5, minHeight: 24 }}
                              >
                                <Icon fontSize='small'>send</Icon>&nbsp;ส่งตอนนี้
                              </MDButton>
                              <MDButton
                                size='small'
                                variant='text'
                                color='dark'
                                aria-label={`แก้เหตุการณ์ ${ev.name}`}
                                onClick={() => openEvEdit(ev)}
                                sx={{ px: 0.5, minHeight: 24 }}
                              >
                                <Icon fontSize='small'>edit</Icon>&nbsp;แก้
                              </MDButton>
                              <MDButton
                                size='small'
                                variant='text'
                                color='error'
                                aria-label={`ลบเหตุการณ์ ${ev.name}`}
                                onClick={() => setEvConfirm({ mode: 'delete', ev })}
                                sx={{ px: 0.5, minHeight: 24 }}
                              >
                                <Icon fontSize='small'>delete</Icon>&nbsp;ลบ
                              </MDButton>
                            </MDBox>
                          )}
                          {ev.custom !== 'Y' &&
                            tplReady &&
                            templates.some((tp) => tp.event === ev.code) && (
                              <MDButton
                                size='small'
                                variant='text'
                                color='info'
                                aria-label={`แก้ข้อความ ${ev.name}`}
                                onClick={() => setTplEvent(ev)}
                                sx={{ px: 0.5, minHeight: 24, mt: 0.25 }}
                              >
                                <Icon fontSize='small'>edit_note</Icon>&nbsp;
                                {templates.some((tp) => tp.event === ev.code && tp.custom === 'Y')
                                  ? 'ข้อความ · แก้แล้ว'
                                  : 'ข้อความ'}
                              </MDButton>
                            )}
                        </TableCell>
                        {activeChannels.map((c) => (
                          <TableCell key={c.id} align='center'>
                            <Checkbox
                              checked={routing[`${ev.code}|${c.id}`] === 'Y'}
                              disabled={!canEdit}
                              inputProps={{ 'aria-label': `${ev.name} → ${c.name}` }}
                              onChange={(e) => toggleRoute(ev.code, Number(c.id), e.target.checked)}
                            />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {activeChannels.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2}>เพิ่มปลายทางก่อน จึงจะผูกเหตุการณ์ได้</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </MDBox>
            </MDBox>
          </Grid>

          {/* ── ประวัติการส่ง ──────────────────────────────────────── */}
          <Grid item xs={12}>
            <MDBox sx={sectionSx}>
              <MDBox display='flex' alignItems='center' justifyContent='space-between'>
                {eyebrow('ประวัติการส่งล่าสุด')}
                <MDButton size='small' variant='text' color='dark' onClick={load}>
                  <Icon>refresh</Icon>&nbsp;รีเฟรช
                </MDButton>
              </MDBox>

              <MDBox mt={1.5} sx={{ overflowX: 'auto' }}>
                <Table size='small'>
                  <TableHead sx={{ display: 'table-header-group' }}>
                    <TableRow>
                      <TableCell>เวลา</TableCell>
                      <TableCell>เหตุการณ์</TableCell>
                      <TableCell>กลุ่ม</TableCell>
                      <TableCell>ผล</TableCell>
                      <TableCell>หมายเหตุ</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {logs.map((l) => (
                      <TableRow key={l.id}>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {whenText(l.created_at)}
                        </TableCell>
                        <TableCell>{l.event_name}</TableCell>
                        <TableCell>{l.channel_name}</TableCell>
                        <TableCell>
                          <Chip
                            size='small'
                            variant={l.status === 'SENT' ? 'outlined' : 'filled'}
                            color={
                              l.status === 'SENT'
                                ? 'success'
                                : l.status === 'FAILED'
                                ? 'error'
                                : undefined
                            }
                            label={
                              l.status === 'SENT'
                                ? 'ส่งแล้ว'
                                : l.status === 'FAILED'
                                ? 'ไม่สำเร็จ'
                                : 'ไม่มีปลายทาง'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <MDTypography
                            variant='caption'
                            color='text'
                            sx={{ opacity: 0.8 }}
                            title={
                              telegramText(l.error_msg) !== String(l.error_msg || '')
                                ? l.error_msg
                                : undefined
                            }
                          >
                            {telegramText(l.error_msg)}
                          </MDTypography>
                        </TableCell>
                        <TableCell align='right' sx={{ whiteSpace: 'nowrap' }}>
                          {canResend(l) && (
                            <MDButton
                              size='small'
                              variant='outlined'
                              color='info'
                              disabled={Boolean(resendingId)}
                              onClick={() => resendLog(l)}
                              title={`ส่งข้อความนี้ซ้ำเข้ากลุ่ม ${l.channel_name}`}
                            >
                              <Icon>replay</Icon>&nbsp;
                              {resendingId === l.id ? 'กำลังส่ง…' : 'ส่งซ้ำ'}
                            </MDButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {logs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6}>ยังไม่มีประวัติการส่ง</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>

      <Dialog
        open={Boolean(testRow)}
        onClose={() => {
          if (!sending) setTestRow(null);
        }}
        fullWidth
        maxWidth='sm'
        aria-labelledby='telegram-test-title'
      >
        <DialogTitle id='telegram-test-title'>ยืนยันส่งข้อความเข้ากลุ่มจริง?</DialogTitle>
        <DialogContent>
          {testRow && (
            <>
              <MDTypography variant='body2' fontWeight='bold'>
                กลุ่ม: {testRow.name}
              </MDTypography>
              <MDTypography variant='caption'>
                ชื่อที่ตั้งไว้ใน ERP · เลขห้อง {testRow.chat_id} · ตรวจให้ตรงกับกลุ่มใน Telegram
              </MDTypography>
              <MDTypography variant='body2' color='error' mt={2}>
                สมาชิกทุกคนในกลุ่มจะได้รับข้อความนี้ ไม่ใช่การทดสอบภายในเครื่อง
              </MDTypography>
              <MDBox
                component='pre'
                p={2}
                mt={2}
                sx={{
                  bgcolor: 'grey.100',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  fontSize: 14,
                }}
              >
                {`🔔 ทดสอบการแจ้งเตือน\nระบบ ERP ส่งข้อความนี้เพื่อยืนยันว่าตั้งค่ากลุ่มถูกต้อง\nกลุ่ม: ${
                  testRow.name
                }\nเวลา: [เวลาที่ส่งจริง] น.\nผู้ทดสอบ: ${app.user.name || '-'}`}
              </MDBox>
              <MDTypography variant='caption'>
                ตัวอย่างข้อความ — เวลาเติมจากเซิร์ฟเวอร์เมื่อส่งจริง
              </MDTypography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            autoFocus
            variant='outlined'
            color='secondary'
            disabled={sending}
            onClick={() => setTestRow(null)}
          >
            ยกเลิก
          </MDButton>
          <MDButton
            variant='gradient'
            color='error'
            disabled={!canTest(testRow) || sending}
            onClick={testSend}
          >
            {sending ? 'กำลังส่ง…' : 'ยืนยันส่งเข้ากลุ่มจริง'}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── ยืนยันปิด/เปิดกลุ่ม ───────────────────────────────────────── */}
      <Dialog
        open={Boolean(confirmRow) && !confirmRow.closing}
        onClose={closeConfirm}
        TransitionProps={{ onExited: () => setConfirmRow(null) }}
        fullWidth
        maxWidth='xs'
        aria-labelledby='channel-enable-title'
      >
        <DialogTitle id='channel-enable-title'>
          {confirmRow && confirmRow.on ? 'เปิดใช้งานกลุ่มนี้อีกครั้ง?' : 'ปิดใช้งานกลุ่มนี้?'}
        </DialogTitle>
        <DialogContent>
          {confirmRow && (
            <>
              <MDTypography variant='body2' fontWeight='bold'>
                กลุ่ม: {confirmRow.name}
              </MDTypography>
              <MDTypography variant='caption' display='block'>
                เลขห้อง {confirmRow.chat_id}
              </MDTypography>
              {confirmRow.on ? (
                <MDTypography
                  variant='body2'
                  mt={2}
                  color={routedCount(confirmRow) > 0 ? 'error' : 'text'}
                >
                  {routedCount(confirmRow) > 0
                    ? `แจ้งเตือน ${routedCount(
                        confirmRow
                      )} เรื่องที่ผูกไว้จะกลับมาส่งเข้ากลุ่มนี้ตั้งแต่ตอนนี้`
                    : 'ยังไม่มีเรื่องที่ผูกกับกลุ่มนี้'}
                </MDTypography>
              ) : (
                <MDTypography variant='body2' mt={2}>
                  ระบบจะหยุดส่งแจ้งเตือนเข้ากลุ่มนี้ทันที · เปิดกลับได้ภายหลัง
                </MDTypography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton autoFocus variant='outlined' color='secondary' onClick={closeConfirm}>
            {t(`element.cancel`)}
          </MDButton>
          <MDButton
            variant='gradient'
            color={confirmRow && confirmRow.on ? 'info' : 'error'}
            onClick={() => setChannelEnable(confirmRow)}
          >
            {confirmRow && confirmRow.on ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>คู่มือสำหรับไอที · ตั้งค่าบอต Telegram</DialogTitle>
        <DialogContent>
          <MDTypography variant='body2' sx={{ whiteSpace: 'pre-line' }}>
            {
              '1. สร้างบอตผ่าน @BotFather และเก็บโทเคนเป็นความลับ\n2. ไอทีใส่โทเคนในไฟล์ตั้งค่าของเซิร์ฟเวอร์ ERP — ห้ามส่งโทเคนในแชตหรือกรอกในหน้านี้\n3. เชิญบอตเข้ากลุ่มที่ต้องการ แล้วให้ไอทีหาเลขห้องตามคู่มือ\n4. กด "เพิ่มกลุ่ม" ใส่ชื่อกลุ่มและเลขห้อง แล้วกด "ทดสอบส่ง"\n5. ติ๊กเลือกว่าเรื่องไหนแจ้งเข้ากลุ่มไหน แล้วดูผลในประวัติการส่ง'
            }
          </MDTypography>
          <MDTypography variant='caption' display='block' mt={2}>
            รายละเอียดเต็มอยู่ในคู่มือตั้งค่าแจ้งเตือน Telegram สำหรับไอที
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setHelpOpen(false)} color='dark'>
            ปิดคู่มือ
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* ── กล่องเพิ่ม/แก้ปลายทาง ─────────────────────────────────────── */}
      {/* ส่งข้อความเองเข้ากลุ่ม (A6) — ส่งแล้วโหลดประวัติใหม่ */}
      <NotifySendDialog
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        token={app.token}
        mod={mod}
        onSent={load}
        onNotify={showNotify}
      />
      {/* เพิ่ม/แก้ เหตุการณ์ของร้าน (0031) — บันทึกแล้วโหลดรายการ+แม่แบบใหม่ */}
      {evDialog && (
        <NotifyEventDialog
          open
          onClose={() => setEvDialog(null)}
          event={evDialog.event}
          initialMessage={evDialog.message}
          token={app.token}
          mod={mod}
          onSaved={load}
          onNotify={showNotify}
        />
      )}
      {/* ยืนยัน ส่งตอนนี้ / ลบ เหตุการณ์ของร้าน */}
      <Dialog
        open={Boolean(evConfirm) && !evConfirm.closing}
        onClose={evBusy ? undefined : closeEvConfirm}
        TransitionProps={{ onExited: () => setEvConfirm(null) }}
        fullWidth
        maxWidth='xs'
        aria-labelledby='custom-event-confirm-title'
      >
        <DialogTitle id='custom-event-confirm-title'>
          {evConfirm && evConfirm.mode === 'send' ? 'ส่งเหตุการณ์นี้ตอนนี้?' : 'ลบเหตุการณ์นี้?'}
        </DialogTitle>
        <DialogContent>
          {evConfirm && (
            <>
              <MDTypography variant='body2' fontWeight='bold'>
                {evConfirm.ev.name}
              </MDTypography>
              {evConfirm.mode === 'send' ? (
                <MDTypography
                  variant='body2'
                  mt={2}
                  color={routedGroups(evConfirm.ev) > 0 ? 'text' : 'error'}
                >
                  {routedGroups(evConfirm.ev) > 0
                    ? `ข้อความจะส่งเข้า ${routedGroups(evConfirm.ev)} กลุ่มที่ติ๊กไว้ทันที`
                    : 'ยังไม่ได้ติ๊กกลุ่มที่จะรับ — ติ๊กในตารางก่อน ไม่งั้นจะไม่มีใครได้รับ'}
                </MDTypography>
              ) : (
                <MDTypography variant='body2' mt={2}>
                  เหตุการณ์ ข้อความ และการผูกกลุ่มจะถูกลบ · ประวัติที่ส่งไปแล้วยังอยู่
                  {evConfirm.ev.schedule_time ? ' · การส่งอัตโนมัติจะหยุดทันที' : ''}
                </MDTypography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton
            autoFocus
            variant='outlined'
            color='secondary'
            disabled={evBusy}
            onClick={closeEvConfirm}
          >
            {t(`element.cancel`)}
          </MDButton>
          <MDButton
            variant='gradient'
            color={evConfirm && evConfirm.mode === 'send' ? 'info' : 'error'}
            disabled={
              evBusy || (evConfirm && evConfirm.mode === 'send' && routedGroups(evConfirm.ev) === 0)
            }
            onClick={runEvConfirm}
          >
            {evBusy ? 'กำลังทำ…' : evConfirm && evConfirm.mode === 'send' ? 'ส่งตอนนี้' : 'ลบ'}
          </MDButton>
        </DialogActions>
      </Dialog>
      {/* หน้าต่างแก้ข้อความแจ้งเตือนต่อเหตุการณ์ (A5) */}
      {tplEvent && (
        <NotifyTemplateDialog
          open
          onClose={() => setTplEvent(null)}
          eventCode={tplEvent.code}
          eventName={tplEvent.name}
          templates={templates}
          canEdit={canEdit}
          token={app.token}
          mod={mod}
          onSaved={reloadTemplates}
          onNotify={showNotify}
        />
      )}
      <Dialog open={Boolean(form)} onClose={() => setForm(null)} fullWidth maxWidth='sm'>
        <DialogTitle>{form && form.id ? 'แก้ไขกลุ่มปลายทาง' : 'เพิ่มกลุ่มปลายทาง'}</DialogTitle>
        <DialogContent>
          {form && (
            <MDBox mt={1} display='flex' flexDirection='column' gap={2.5}>
              <MDInput
                label='ชื่อกลุ่ม (ที่แสดงในระบบ)'
                placeholder='เช่น กลุ่มการตลาด'
                fullWidth
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <MDInput
                label='ช่องทาง'
                fullWidth
                select
                value={form.channel}
                onChange={(e) => setForm({ ...form, channel: e.target.value })}
                InputLabelProps={{ shrink: true }}
              >
                <MenuItem value='TELEGRAM'>Telegram</MenuItem>
              </MDInput>
              <MDInput
                label='เลขห้องแชท'
                title='chat id ของกลุ่ม Telegram'
                placeholder='กลุ่มขึ้นต้นด้วยเครื่องหมายลบ เช่น -1001234567890'
                fullWidth
                value={form.chat_id}
                onChange={(e) => setForm({ ...form, chat_id: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <MDInput
                label='บันทึกช่วยจำ'
                placeholder='เช่น ใครดูแลกลุ่มนี้ ใช้แจ้งเรื่องอะไร'
                fullWidth
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
              <MDTypography variant='caption' color='text' sx={{ opacity: 0.75 }}>
                ไม่รู้เลขห้อง? ให้ไอทีหาให้ (ขั้นตอนอยู่ในคู่มือสำหรับไอที)
              </MDTypography>
              {events.length > 0 && (
                <MDBox
                  sx={{ border: '1px solid', borderColor: 'grey.300', borderRadius: 2, p: 1.5 }}
                >
                  <MDBox display='flex' justifyContent='space-between' alignItems='center' mb={0.5}>
                    <MDTypography variant='button' fontWeight='medium'>
                      กลุ่มนี้รับแจ้งเรื่องไหนบ้าง · {(form.events || []).length}/{events.length}
                    </MDTypography>
                    <MDBox display='flex' gap={0.5}>
                      <MDButton
                        size='small'
                        variant='text'
                        color='info'
                        sx={{ minHeight: 24, px: 0.5 }}
                        onClick={() => setForm({ ...form, events: events.map((ev) => ev.code) })}
                      >
                        เลือกทั้งหมด
                      </MDButton>
                      <MDButton
                        size='small'
                        variant='text'
                        color='secondary'
                        sx={{ minHeight: 24, px: 0.5 }}
                        onClick={() => setForm({ ...form, events: [] })}
                      >
                        ล้าง
                      </MDButton>
                    </MDBox>
                  </MDBox>
                  <MDBox sx={{ maxHeight: 220, overflowY: 'auto' }}>
                    {events.map((ev) => {
                      const on = (form.events || []).includes(ev.code);
                      return (
                        <MDBox key={ev.code} display='flex' alignItems='center' gap={0.5}>
                          <Checkbox
                            size='small'
                            checked={on}
                            inputProps={{ 'aria-label': `กลุ่มนี้รับ ${ev.name}` }}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                events: e.target.checked
                                  ? [...(form.events || []), ev.code]
                                  : (form.events || []).filter((x) => x !== ev.code),
                              })
                            }
                          />
                          <MDTypography
                            variant='button'
                            sx={{ cursor: 'pointer' }}
                            onClick={() =>
                              setForm({
                                ...form,
                                events: on
                                  ? (form.events || []).filter((x) => x !== ev.code)
                                  : [...(form.events || []), ev.code],
                              })
                            }
                          >
                            {ev.name}
                            {ev.custom === 'Y' && (
                              <MDTypography component='span' variant='caption' color='info'>
                                {' '}
                                · ของร้าน
                              </MDTypography>
                            )}
                          </MDTypography>
                        </MDBox>
                      );
                    })}
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          )}
        </DialogContent>
        <DialogActions>
          <MDButton variant='outlined' color='secondary' onClick={() => setForm(null)}>
            {t(`element.cancel`)}
          </MDButton>
          <MDButton variant='gradient' color='dark' onClick={saveChannel}>
            <Icon>save</Icon>&nbsp;{t(`element.save`)}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
      {notify}

      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    </DashboardLayout>
  );
}

export default NotifySetting;
